'use client';

import { useEffect } from 'react';

import { capturePostHogEvent } from '@/lib/posthog';

const CTA_SELECTOR = '[data-analytics-cta-id]';

function captureTrackedCta(event: MouseEvent): void {
  if (event.type === 'click' && event.button !== 0) return;
  if (event.type === 'auxclick' && event.button !== 1) return;
  if (!(event.target instanceof Element)) return;

  const element = event.target.closest<HTMLElement>(CTA_SELECTOR);
  if (!element) return;

  const ctaId = element.dataset.analyticsCtaId;
  const location = element.dataset.analyticsLocation;
  if (!ctaId || !location) return;

  const destination = element.dataset.analyticsDestination
    ?? (element instanceof HTMLAnchorElement ? element.getAttribute('href') ?? undefined : undefined);

  capturePostHogEvent(
    'cta_clicked',
    {
      cta_id: ctaId,
      location,
      ...(destination ? { destination } : {}),
    },
    { send_instantly: true, transport: 'sendBeacon' },
  );
}

/** Captures declaratively tagged CTA clicks without altering navigation. */
export function PostHogCtaListener() {
  useEffect(() => {
    document.addEventListener('click', captureTrackedCta, true);
    document.addEventListener('auxclick', captureTrackedCta, true);
    return () => {
      document.removeEventListener('click', captureTrackedCta, true);
      document.removeEventListener('auxclick', captureTrackedCta, true);
    };
  }, []);

  return null;
}